import { Amount } from '@core/types/amount';
import { CountryIsoCode } from '@core/types/country';

export interface PaginationData {
  current: number;
  size: number;
  totalPages: number;
  totalResults: number;
}

export enum ContactFilterName {
  Segment = 'segment',
}

export enum ContactOrigin {
  Ankorstore = 'ankorstore',
  Imported = 'imported',
  Klaviyo = 'klaviyo',
  Event = 'event',
  ManualImport = 'manual_import',
  PromoteLink = 'promote_link',
}

export enum LiftEligibility {
  Eligible = 'eligible',
  Claimed = 'claimed',
  Ineligible = 'ineligible',
}

export enum SortColumns {
  shop_name = 'retailer',
  total_spent = 'ordering.totalSpent.amount',
  last_purchase = 'ordering.lastOrderedAt.range',
  eligible_lift = 'ordering.liftEligible',
  commission_fees = 'commercialTerms.serviceFees',
  personalised_discount = 'commercialTerms.brandDiscount',
}

export enum SubscriptionStatus {
  subscribed = 'subscribed',
  unsubscribed = 'unsubscribed',
  suppressed = 'suppressed',
}

// Platform-level categorical activity signal derived by the backend (at index time) from the
// retailer's last order date across all brands (see ActivitySignal::fromLastOrderedAt). Lives on the
// ordering object and is exposed as a label, never a raw date.
export enum ActivitySignal {
  High = 'high',
  Low = 'low',
  Inactive = 'inactive',
}

export interface RetailerContact {
  id: number;
  uuid: string;
  name: string;
  shopType: string;
  createdAt: string;
  contact: {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    country: CountryIsoCode;
    language: string;
    city: string;
    address: string;
    zip: string;
    website: string;
  };
  aksPlus: {
    active: boolean;
    eligible: boolean;
  };
}

export interface ContactItem {
  type: 'contact-document';
  attributes: {
    id: number;
    uuid: string;
    qrCampaignName?: string;
    contact: {
      firstname: string;
      lastname: string;
      email: string;
      language: string;
      source?: string;
      shopName?: string;
      phone: string;
      address: string;
      city: string;
      country: CountryIsoCode;
      zip: string;
      website: string;
    };
    relation: {
      addedAt: string;
      invitedAt: string;
      affiliatedAt: string;
      status: string;
      origin: ContactOrigin;
    };
    retailer: RetailerContact;
    commercialTerms: {
      serviceFees: number;
      brandDiscount: number;
      repeatOffer: number;
      hasRepeatOffer: boolean;
      hasBrandDiscount: boolean;
      amountToUnlockRepeatOffer: number;
      freeShippingForBrandUntil: string;
      repeatOfferUnlockedUntil: string;
      tieredDiscount: {
        id: string;
        name: string;
        percentage: number;
      };
    };
    ordering: {
      lastOrderedAt: string;
      lastOrderedAmount: Amount;
      totalCompletedOrders: number;
      totalSpent: Amount;
      liftEligible: boolean;
      liftRedeemed: boolean;
      firstOrderedAt?: string;
      // Average days between this retailer's orders with the brand (90-day fallback for single-order
      // contacts). Used to derive the "days overdue" value shown in the Reorder segment.
      avgOrderFrequencyDays?: number | null;
      // Date the retailer is expected to reorder (last order + average order frequency).
      expectedNextOrderAt?: string | null;
      // Date past which the retailer is considered disengaged (last order + churn window). The
      // reorder delay turns orange once 75% of the expectedNextOrderAt → disengagedAt window elapses.
      disengagedAt?: string | null;
      activitySignal?: ActivitySignal;
    };
    preparedOrders: {
      number: number;
    };
    cart: {
      amountInCart: Amount;
      cartUpdatedAt: string;
      isPreparedOrder?: boolean;
    };
    communication: {
      messaging: {
        lastContactedAt: string;
        lastMessageBy: string;
      };
      emailing: {
        lastContactedAt: string;
        subscriptionStatus: SubscriptionStatus;
        contactedRecently: boolean;
      };
    };
  };
}
